/**
 * A type guard that checks for plain objects (and excludes arrays, null, etc.).
 */
function isObject(obj: unknown): obj is Record<string, unknown> {
  return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
}

/**
 * Deeply merges one or more source objects into the target.
 * - Only plain objects are merged recursively.
 * - Arrays and other values are overwritten by the last source.
 *
 * @param target   The object to receive properties.
 * @param sources  One or more source objects whose properties will be merged in.
 * @returns        The modified target (and its narrowed type).
 */
function deepMerge<T extends object>(target: T, ...sources: Partial<T>[]): T {
  for (const source of sources) {
    if (!isObject(source)) continue;

    for (const key of Object.keys(source) as Array<keyof T>) {
      const srcVal = source[key];
      const tgtVal = target[key];

      // Recurse if both are plain objects
      if (isObject(srcVal) && isObject(tgtVal)) {
        deepMerge(tgtVal, srcVal as Partial<T[keyof T]>);
      } else {
        // Otherwise overwrite
        (target[key] as unknown) = srcVal;
      }
    }
  }
  return target;
}

export { deepMerge };
